import React, { useEffect, useState } from 'react';
import { BookOpen, Bot, Copy, KeyRound, Plus, RefreshCw, Route, ScrollText, Sparkles, X } from 'lucide-react';
import { llmGatewayApi } from '../../llm-gateway/llmGatewayApi';
import type { LlmGateway } from '../../llm-gateway/llmGatewayTypes';
import {
  CLIENT_AUTH_OPTIONS,
  CLIENT_TYPE_OPTIONS,
  LLM_ROUTE_CLAIM_HELP,
  MCP_PUBLIC_URL,
  SINGLETON_ENDPOINT_ID,
  claimsFromRouteToken,
  clientAuthLabel,
  clientTypeLabel,
  generateRegistrationId,
  isGeneratedRegistrationId,
  maskSecret,
} from '../governedMcpConstants';
import { governedMcpApi } from '../governedMcpApi';
import { logGovernedMcp } from '../governedMcpLogger';
import { createId } from '../governedMcpStore';
import type { ClientAuthOption, ClientType, LlmRouteTokenClaims, McpClient } from '../governedMcpTypes';
import { FormSection, FormStack, GatewayDrawer, fieldClass, ghostBtn, helperClass, labelClass, panelClass, primaryBtn } from '../components/GatewayUi';
import ConfirmDialog from '../components/ConfirmDialog';
import LlmRouteConfigureDialog from './LlmRouteConfigureDialog';

function needsAdminKey(type: ClientType): boolean {
  return type === 'claude_enterprise' || type === 'chatgpt_enterprise';
}

function emptyRouteFields(): Pick<McpClient, 'llmRouteEnabled' | 'llmGatewayId' | 'llmRouteToken' | 'llmRouteExpiresAt'> {
  return {
    llmRouteEnabled: false,
    llmGatewayId: '',
    llmRouteToken: '',
    llmRouteExpiresAt: '',
  };
}

function blankClient(occupiedRegistrationIds: string[]): McpClient {
  const clientType: ClientType = 'claude_enterprise';
  return {
    id: createId('cl'),
    name: '',
    clientType,
    registrationId: generateRegistrationId(clientType, occupiedRegistrationIds),
    authConfig: '',
    authOption: '',
    adminKey: '',
    workspaceIds: [],
    customKeyLabel: '',
    customKeyValue: '',
    ...emptyRouteFields(),
    endpointId: SINGLETON_ENDPOINT_ID,
    environment: 'development',
    trustPolicy: 'enterprise_managed',
    allowedUserIds: [],
    allowedGroupIds: [],
    status: 'active',
    createdBy: 'Current administrator',
    lastConnectedAt: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function hydrateClient(initial: McpClient): McpClient {
  const authOption: ClientAuthOption =
    initial.authOption === 'oauth' || initial.authOption === 'api_key'
      ? initial.authOption
      : '';
  const workspaceIds = initial.workspaceIds && initial.workspaceIds.length > 0
    ? initial.workspaceIds
    : initial.clientType === 'chatgpt_enterprise'
      ? ['']
      : [];
  return {
    ...initial,
    endpointId: SINGLETON_ENDPOINT_ID,
    authOption,
    adminKey: initial.adminKey ?? '',
    workspaceIds,
    customKeyLabel: initial.customKeyLabel ?? '',
    customKeyValue: initial.customKeyValue ?? '',
    llmRouteEnabled: Boolean(initial.llmRouteEnabled),
    llmGatewayId: initial.llmGatewayId ?? '',
    llmRouteToken: initial.llmRouteToken ?? '',
    llmRouteExpiresAt: initial.llmRouteExpiresAt ?? '',
  };
}

function buildSavedClient(form: McpClient): McpClient {
  const authOption: ClientAuthOption = form.authOption === 'oauth' || form.authOption === 'api_key' ? form.authOption : '';
  const {
    adminKey: _adminKey,
    workspaceIds: _workspaceIds,
    customKeyLabel: _customKeyLabel,
    customKeyValue: _customKeyValue,
    llmRouteEnabled: _llmRouteEnabled,
    llmGatewayId: _llmGatewayId,
    llmRouteToken: _llmRouteToken,
    llmRouteExpiresAt: _llmRouteExpiresAt,
    ...rest
  } = form;
  const next: McpClient = {
    ...rest,
    endpointId: SINGLETON_ENDPOINT_ID,
    environment: 'production',
    authOption,
    authConfig: clientAuthLabel(authOption),
    ...emptyRouteFields(),
  };
  if (needsAdminKey(form.clientType)) {
    next.adminKey = form.adminKey?.trim() || '';
  }
  if (form.clientType === 'chatgpt_enterprise') {
    next.workspaceIds = (form.workspaceIds || []).map((id) => id.trim()).filter(Boolean);
  }
  if (form.clientType === 'custom') {
    next.customKeyLabel = form.customKeyLabel?.trim() || '';
    next.customKeyValue = form.customKeyValue?.trim() || '';
    if (form.llmRouteEnabled) {
      next.llmRouteEnabled = true;
      next.llmGatewayId = form.llmGatewayId?.trim() || '';
      next.llmRouteToken = form.llmRouteToken?.trim() || '';
      next.llmRouteExpiresAt = form.llmRouteExpiresAt?.trim() || '';
    }
  }
  return next;
}

function formatClaimValue(key: keyof LlmRouteTokenClaims, value: string | number): string {
  if ((key === 'iat' || key === 'exp') && typeof value === 'number' && value > 0) {
    return new Date(value * 1000).toLocaleString();
  }
  return String(value || '—');
}

export default function ClientFormDrawer({
  open,
  initial,
  occupiedRegistrationIds = [],
  isLight,
  onClose,
  onSave,
  onPersistClient,
  onToast,
}: {
  open: boolean;
  initial: McpClient | null;
  occupiedRegistrationIds?: string[];
  isLight: boolean;
  onClose: () => void;
  onSave: (client: McpClient) => void;
  onPersistClient?: (client: McpClient) => void;
  onToast?: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const [form, setForm] = useState<McpClient>(() => blankClient(occupiedRegistrationIds));
  const [error, setError] = useState<string | null>(null);
  const [gateways, setGateways] = useState<LlmGateway[]>([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(false);
  const [gatewaysError, setGatewaysError] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [claims, setClaims] = useState<LlmRouteTokenClaims | null>(null);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const next = initial ? hydrateClient(initial) : blankClient(occupiedRegistrationIds);
    setForm(next);
    setError(null);
    setConfigureOpen(false);
    setRotateOpen(false);
    setClaims(next.llmRouteToken ? claimsFromRouteToken(next.llmRouteToken) : null);
    // Reset only when the drawer opens or the edited client changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const isEditing = Boolean(initial);
  const routeEnabled = form.clientType === 'custom' && Boolean(form.llmRouteEnabled);

  useEffect(() => {
    if (!open || !routeEnabled) return;
    let cancelled = false;
    setGatewaysLoading(true);
    setGatewaysError(null);
    logGovernedMcp('app', 'client_llm_gateways_load', { clientId: form.id });
    llmGatewayApi
      .listGateways()
      .then((items) => {
        if (cancelled) return;
        setGateways(items);
        logGovernedMcp('app', 'client_llm_gateways_loaded', { clientId: form.id, count: items.length });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load LLM gateways.';
        setGatewaysError(message);
        logGovernedMcp('error', 'client_llm_gateways_failed', { clientId: form.id, message });
      })
      .finally(() => {
        if (!cancelled) setGatewaysLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.id, open, routeEnabled]);

  const applyClientType = (nextType: ClientType) => {
    setForm((prev) => {
      const shouldRegenerate = !isEditing && isGeneratedRegistrationId(prev.registrationId);
      const nextId = shouldRegenerate
        ? generateRegistrationId(nextType, [...occupiedRegistrationIds, prev.registrationId])
        : prev.registrationId;
      return {
        ...prev,
        clientType: nextType,
        registrationId: nextId,
        adminKey: needsAdminKey(nextType) ? prev.adminKey : '',
        workspaceIds: nextType === 'chatgpt_enterprise' ? (prev.workspaceIds?.length ? prev.workspaceIds : ['']) : [],
        customKeyLabel: nextType === 'custom' ? prev.customKeyLabel : '',
        customKeyValue: nextType === 'custom' ? prev.customKeyValue : '',
        ...(nextType === 'custom' ? {} : emptyRouteFields()),
      };
    });
    if (nextType !== 'custom') {
      setClaims(null);
      setConfigureOpen(false);
    }
  };

  const generateId = () => {
    if (isEditing) return;
    const next = generateRegistrationId(form.clientType, [...occupiedRegistrationIds, form.registrationId]);
    setForm((prev) => ({ ...prev, registrationId: next }));
    logGovernedMcp('app', 'client_registration_id_generated', { id: form.id, clientType: form.clientType });
  };

  const failValidation = (reason: string, message: string) => {
    logGovernedMcp('warning', 'client_form_validation_failed', { id: form.id, clientType: form.clientType, reason });
    setError(message);
  };

  const notify = (type: 'success' | 'error' | 'info', message: string) => {
    onToast?.(type, message);
  };

  const copyValue = async (value: string, label: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      notify('success', `${label} copied`);
      logGovernedMcp('app', 'client_llm_route_copied', { id: form.id, label });
    } catch {
      notify('error', 'Could not copy to clipboard');
      logGovernedMcp('warning', 'client_llm_route_copy_failed', { id: form.id, label });
    }
  };

  const issueRouteToken = async (gatewayId: string, persist: boolean) => {
    const registrationId = form.registrationId.trim();
    if (!registrationId) {
      failValidation('registration_id_required', 'Registration identifier is required before issuing a route token.');
      return null;
    }
    setTokenLoading(true);
    setError(null);
    logGovernedMcp('app', persist ? 'client_llm_route_rotate' : 'client_llm_route_issue', {
      id: form.id,
      llmGatewayId: gatewayId,
      persist,
    });
    try {
      const issued = await governedMcpApi.issueLlmRouteToken({
        clientId: form.id,
        llmGatewayId: gatewayId,
        registrationId,
        persist,
      });
      setForm((prev) => ({
        ...prev,
        llmRouteEnabled: true,
        llmGatewayId: gatewayId,
        llmRouteToken: issued.token,
        llmRouteExpiresAt: issued.expiresAt,
      }));
      setClaims(issued.claims);
      if (persist) {
        onPersistClient?.(
          buildSavedClient({
            ...form,
            llmRouteEnabled: true,
            llmGatewayId: gatewayId,
            llmRouteToken: issued.token,
            llmRouteExpiresAt: issued.expiresAt,
          })
        );
      }
      logGovernedMcp('app', persist ? 'client_llm_route_rotate_ok' : 'client_llm_route_issue_ok', {
        id: form.id,
        llmGatewayId: gatewayId,
        expiresAt: issued.expiresAt,
      });
      return issued;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to issue the LLM route token.';
      setError(message);
      logGovernedMcp('error', persist ? 'client_llm_route_rotate_failed' : 'client_llm_route_issue_failed', {
        id: form.id,
        llmGatewayId: gatewayId,
        message,
      });
      notify('error', message);
      return null;
    } finally {
      setTokenLoading(false);
    }
  };

  const applyGateway = (gatewayId: string) => {
    setForm((prev) => ({
      ...prev,
      llmGatewayId: gatewayId,
      llmRouteToken: '',
      llmRouteExpiresAt: '',
    }));
    setClaims(null);
    if (gatewayId) void issueRouteToken(gatewayId, false);
  };

  const toggleRoute = (enabled: boolean) => {
    if (!enabled) {
      setForm((prev) => ({ ...prev, ...emptyRouteFields() }));
      setClaims(null);
      setConfigureOpen(false);
      logGovernedMcp('app', 'client_llm_route_disabled', { id: form.id });
      return;
    }
    setForm((prev) => ({ ...prev, llmRouteEnabled: true }));
    logGovernedMcp('app', 'client_llm_route_enabled', { id: form.id });
  };

  const submit = async () => {
    if (!form.name.trim()) {
      failValidation('name_required', 'Client name is required.');
      return;
    }
    if (!form.registrationId.trim()) {
      failValidation('registration_id_required', 'Registration identifier is required.');
      return;
    }
    if (occupiedRegistrationIds.includes(form.registrationId.trim())) {
      failValidation('registration_id_taken', 'Registration identifier is already in use.');
      return;
    }
    if (form.clientType === 'custom') {
      const label = form.customKeyLabel?.trim() || '';
      const value = form.customKeyValue?.trim() || '';
      if (Boolean(label) !== Boolean(value)) {
        failValidation('custom_key_pair', 'Provide both a key label and value, or leave both empty.');
        return;
      }
      if (form.llmRouteEnabled) {
        if (!form.llmGatewayId?.trim()) {
          failValidation('llm_gateway_required', 'Select a registered LLM gateway for this tenant.');
          return;
        }
        const selected = gateways.find((item) => item.id === form.llmGatewayId);
        if (selected && selected.status !== 'active') {
          failValidation('llm_gateway_inactive', 'Select an active LLM gateway.');
          return;
        }
        const currentClaims = claims || (form.llmRouteToken ? claimsFromRouteToken(form.llmRouteToken) : null);
        const claimsMatch =
          Boolean(form.llmRouteToken) &&
          currentClaims?.llm_gateway_id === form.llmGatewayId &&
          currentClaims.registration_id === form.registrationId.trim();
        if (!claimsMatch) {
          const issued = await issueRouteToken(form.llmGatewayId, false);
          if (!issued) return;
          onSave(
            buildSavedClient({
              ...form,
              llmRouteEnabled: true,
              llmGatewayId: form.llmGatewayId,
              llmRouteToken: issued.token,
              llmRouteExpiresAt: issued.expiresAt,
            })
          );
          return;
        }
      }
    }
    onSave(buildSavedClient(form));
  };

  const workspaceIds = form.workspaceIds && form.workspaceIds.length > 0 ? form.workspaceIds : [''];
  const authLabel = clientAuthLabel(form.authOption);
  const selectedGateway = gateways.find((item) => item.id === form.llmGatewayId) || null;
  const tenantId = claims?.tenant_id || '';

  return (
    <GatewayDrawer
      open={open}
      eyebrow="Governed MCP / Clients"
      icon={<Bot size={18} className="text-indigo-500" />}
      title={initial ? form.name || 'Edit MCP client' : 'Register AI client'}
      subtitle="The client requests. Selfic maps identity and decides what it may discover or invoke."
      status={{ label: form.status === 'active' ? 'Active' : form.status, tone: form.status === 'active' ? 'success' : 'neutral' }}
      isLight={isLight}
      size="xl"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryBtn()} onClick={() => void submit()} disabled={tokenLoading}>
            {initial ? 'Save client' : 'Register client'}
          </button>
        </>
      }
    >
      {error ? <p className="mb-4 text-sm text-red-500">{error}</p> : null}
      <FormStack>
        <div className="space-y-5">
          <FormSection title="Identity" description="How this AI application is registered in Selfic." isLight={isLight}>
            <div className="space-y-4">
              <div>
                <label className={labelClass(isLight)}>Client name</label>
                <input className={fieldClass(isLight)} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Claude Enterprise — Risk Desk" />
              </div>
              <div>
                <label className={labelClass(isLight)}>Client type</label>
                <select className={fieldClass(isLight)} value={form.clientType} onChange={(e) => applyClientType(e.target.value as ClientType)}>
                  {CLIENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                  {CLIENT_TYPE_OPTIONS.find((option) => option.value === form.clientType)?.hint}
                </p>
              </div>
              <div>
                <label className={labelClass(isLight)}>Registration identifier</label>
                <div className="flex gap-2">
                  <input
                    className={`${fieldClass(isLight)} font-mono`}
                    value={form.registrationId}
                    onChange={(e) => setForm((p) => ({ ...p, registrationId: e.target.value }))}
                    placeholder="claude_482913"
                  />
                  <button type="button" className={`${ghostBtn(isLight)} shrink-0`} onClick={generateId} disabled={isEditing}>
                    <Sparkles size={14} /> Generate
                  </button>
                </div>
              </div>
              <div>
                <label className={labelClass(isLight)}>Authentication</label>
                <select
                  className={fieldClass(isLight)}
                  value={form.authOption || ''}
                  onChange={(e) => setForm((p) => ({ ...p, authOption: e.target.value as ClientAuthOption }))}
                >
                  {CLIENT_AUTH_OPTIONS.map((option) => (
                    <option key={option.value || 'none'} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                  Optional. Choose how this client authenticates to the gateway.
                </p>
              </div>
              {needsAdminKey(form.clientType) ? (
                <div>
                  <label className={labelClass(isLight)}>Admin key (optional)</label>
                  <input
                    type="password"
                    autoComplete="off"
                    className={fieldClass(isLight)}
                    value={form.adminKey || ''}
                    onChange={(e) => setForm((p) => ({ ...p, adminKey: e.target.value }))}
                    placeholder="Enter the organization admin key"
                  />
                </div>
              ) : null}
              {form.clientType === 'chatgpt_enterprise' ? (
                <div>
                  <label className={labelClass(isLight)}>Workspace ids (optional)</label>
                  <div className="space-y-2">
                    {workspaceIds.map((workspaceId, index) => (
                      <div key={`workspace-${index}`} className="flex gap-2">
                        <input
                          className={`${fieldClass(isLight)} font-mono`}
                          value={workspaceId}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForm((p) => {
                              const current = p.workspaceIds && p.workspaceIds.length > 0 ? [...p.workspaceIds] : [''];
                              current[index] = value;
                              return { ...p, workspaceIds: current };
                            });
                          }}
                          placeholder="ws-…"
                        />
                        {workspaceIds.length > 1 ? (
                          <button
                            type="button"
                            className={`${ghostBtn(isLight)} shrink-0 px-2`}
                            onClick={() => {
                              setForm((p) => {
                                const current = (p.workspaceIds || []).filter((_, itemIndex) => itemIndex !== index);
                                return { ...p, workspaceIds: current.length > 0 ? current : [''] };
                              });
                            }}
                            aria-label="Remove workspace id"
                          >
                            <X size={14} />
                          </button>
                        ) : null}
                      </div>
                    ))}
                    <button
                      type="button"
                      className={ghostBtn(isLight)}
                      onClick={() => setForm((p) => ({ ...p, workspaceIds: [...(p.workspaceIds && p.workspaceIds.length ? p.workspaceIds : ['']), ''] }))}
                    >
                      <Plus size={14} /> Add workspace
                    </button>
                  </div>
                </div>
              ) : null}
              {form.clientType === 'custom' ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass(isLight)}>Key label (optional)</label>
                    <input
                      className={fieldClass(isLight)}
                      value={form.customKeyLabel || ''}
                      onChange={(e) => setForm((p) => ({ ...p, customKeyLabel: e.target.value }))}
                      placeholder="Authorization"
                    />
                  </div>
                  <div>
                    <label className={labelClass(isLight)}>Key value (optional)</label>
                    <input
                      type="password"
                      autoComplete="off"
                      className={fieldClass(isLight)}
                      value={form.customKeyValue || ''}
                      onChange={(e) => setForm((p) => ({ ...p, customKeyValue: e.target.value }))}
                      placeholder="Secret value"
                    />
                  </div>
                </div>
              ) : null}
              {form.clientType === 'custom' ? (
                <div className={`rounded-xl border p-4 space-y-4 ${isLight ? 'border-neutral-200 bg-neutral-50/70' : 'border-neutral-800 bg-neutral-950/50'}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className={`mt-1 rounded text-indigo-600 focus:ring-indigo-500 ${isLight ? 'border-neutral-300' : 'border-neutral-600 bg-neutral-900'}`}
                      checked={Boolean(form.llmRouteEnabled)}
                      onChange={(e) => toggleRoute(e.target.checked)}
                    />
                    <span className="min-w-0">
                      <span className={`flex items-center gap-2 text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                        <Route size={15} className="text-indigo-500" />
                        Enable LLM Gateway route
                      </span>
                      <span className={helperClass(isLight)}>
                        Bind this custom client to one LLM gateway registered for the current tenant. The signed JWT stays tenant-scoped.
                      </span>
                    </span>
                  </label>

                  {form.llmRouteEnabled ? (
                    <div className="space-y-4">
                      <div>
                        <label className={labelClass(isLight)}>LLM gateway</label>
                        <select
                          className={fieldClass(isLight)}
                          value={form.llmGatewayId || ''}
                          disabled={gatewaysLoading || tokenLoading}
                          onChange={(e) => applyGateway(e.target.value)}
                        >
                          <option value="">{gatewaysLoading ? 'Loading tenant gateways…' : 'Select a tenant LLM gateway'}</option>
                          {gateways.map((gateway) => (
                            <option key={gateway.id} value={gateway.id} disabled={gateway.status !== 'active'}>
                              {gateway.name}
                              {gateway.publicAlias ? ` · ${gateway.publicAlias}` : ''}
                              {` · ${gateway.status}`}
                            </option>
                          ))}
                        </select>
                        {gatewaysError ? <p className="text-xs mt-1 text-red-500">{gatewaysError}</p> : null}
                        {!gatewaysLoading && !gatewaysError && gateways.length === 0 ? (
                          <p className={helperClass(isLight)}>No LLM gateways are registered for this tenant.</p>
                        ) : (
                          <p className={helperClass(isLight)}>
                            Draft and disabled gateways stay visible. Only an active gateway can receive a route token.
                          </p>
                        )}
                      </div>

                      {form.llmGatewayId ? (
                        <div className="space-y-3">
                          <div>
                            <label className={labelClass(isLight)}>
                              <span className="inline-flex items-center gap-1.5">
                                <KeyRound size={14} className="text-indigo-500" />
                                Route JWT
                              </span>
                            </label>
                            <div className="flex gap-2">
                              <input
                                className={`${fieldClass(isLight)} font-mono`}
                                value={form.llmRouteToken ? maskSecret(form.llmRouteToken) : tokenLoading ? 'Issuing token…' : ''}
                                readOnly
                                placeholder="Select a gateway to issue a token"
                              />
                              <button
                                type="button"
                                className={`${ghostBtn(isLight)} shrink-0`}
                                disabled={!form.llmRouteToken}
                                onClick={() => void copyValue(form.llmRouteToken || '', 'Route token')}
                              >
                                <Copy size={14} /> Copy
                              </button>
                              <button
                                type="button"
                                className={`${ghostBtn(isLight)} shrink-0`}
                                disabled={!form.llmGatewayId || tokenLoading}
                                onClick={() => setRotateOpen(true)}
                              >
                                <RefreshCw size={14} className={tokenLoading ? 'animate-spin' : ''} /> Rotate
                              </button>
                            </div>
                            <p className={helperClass(isLight)}>
                              {form.llmRouteExpiresAt
                                ? `Expires ${new Date(form.llmRouteExpiresAt).toLocaleString()}. Rotate replaces the previous token immediately.`
                                : 'The token is issued for the signed-in tenant. Copy it now; the secret is not shown again in full.'}
                            </p>
                          </div>

                          {claims ? (
                            <div>
                              <p className={`${labelClass(isLight)} inline-flex items-center gap-1.5`}>
                                <ScrollText size={14} className="text-indigo-500" />
                                Token claims
                              </p>
                              <dl className={`rounded-xl border divide-y ${isLight ? 'border-neutral-200 bg-white divide-neutral-100' : 'border-neutral-800 bg-neutral-900 divide-neutral-800'}`}>
                                {LLM_ROUTE_CLAIM_HELP.map((item) => (
                                  <div key={item.key} className="px-3 py-2.5">
                                    <div className="flex items-start justify-between gap-3">
                                      <dt className={`font-mono text-[11px] ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.label}</dt>
                                      <dd className={`text-xs font-medium text-right break-all ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                                        {formatClaimValue(item.key, claims[item.key])}
                                      </dd>
                                    </div>
                                    <p className={`text-[11px] mt-1 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.meaning}</p>
                                  </div>
                                ))}
                              </dl>
                            </div>
                          ) : null}

                          <button
                            type="button"
                            className={ghostBtn(isLight)}
                            onClick={() => {
                              setConfigureOpen(true);
                              logGovernedMcp('app', 'client_llm_route_configure_open', { id: form.id, llmGatewayId: form.llmGatewayId });
                            }}
                          >
                            <BookOpen size={14} /> How to configure
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </FormSection>
        </div>

        <div className="space-y-4">
          <section className={`${panelClass(isLight)} p-5`}>
            <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Registration</h3>
            <dl className={`mt-3 space-y-3 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              <div>
                <dt>Type</dt>
                <dd className={isLight ? 'text-neutral-900' : 'text-white'}>{clientTypeLabel(form.clientType)}</dd>
              </div>
              <div>
                <dt>Identifier</dt>
                <dd className={`font-mono break-all ${isLight ? 'text-neutral-900' : 'text-white'}`}>{form.registrationId || '—'}</dd>
              </div>
              <div>
                <dt>MCP URL</dt>
                <dd className={`font-mono break-all ${isLight ? 'text-neutral-900' : 'text-white'}`}>{MCP_PUBLIC_URL}</dd>
              </div>
              <div>
                <dt>Auth</dt>
                <dd className={isLight ? 'text-neutral-900' : 'text-white'}>{authLabel || 'Not specified'}</dd>
              </div>
              {form.clientType === 'custom' && form.llmRouteEnabled ? (
                <div>
                  <dt>LLM route</dt>
                  <dd className={isLight ? 'text-neutral-900' : 'text-white'}>
                    {selectedGateway?.name || form.llmGatewayId || 'Gateway not selected'}
                  </dd>
                </div>
              ) : null}
            </dl>
          </section>
          <section className={`${panelClass(isLight)} p-5`}>
            <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Identity rule</h3>
            <p className={`text-sm mt-2 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              Client-asserted user ids are ignored. The IdP subject is mapped to the enterprise user, then existing Selfic permissions apply.
            </p>
          </section>
        </div>
      </FormStack>

      <LlmRouteConfigureDialog
        open={configureOpen}
        isLight={isLight}
        gatewayId={form.llmGatewayId || ''}
        gatewayAlias={selectedGateway?.publicAlias || claims?.gateway_alias || ''}
        tenantId={tenantId}
        clientId={form.id}
        token={form.llmRouteToken || ''}
        onClose={() => setConfigureOpen(false)}
        onCopy={(value, label) => void copyValue(value, label)}
      />
      <ConfirmDialog
        open={rotateOpen}
        title="Rotate the route token?"
        description="The current JWT stops working as soon as the new token is issued. Update every client that still uses the previous secret."
        confirmLabel="Rotate token"
        danger
        isLight={isLight}
        onCancel={() => setRotateOpen(false)}
        onConfirm={() => {
          setRotateOpen(false);
          if (!form.llmGatewayId) return;
          void issueRouteToken(form.llmGatewayId, isEditing).then((issued) => {
            if (issued) notify('success', 'Route token rotated');
          });
        }}
      />
    </GatewayDrawer>
  );
}
